/**
 * Search typeahead results panel.
 */
import {header2Search} from '@amblique/king-living/app/theme/foundations/header-2-search';

export const listSiblingHover = header2Search.listHover;

export default {
  parts: [
    'wrap',
    'section',
    'heading',
    'list',
    'item',
    'itemInner',
    'itemImage',
    'itemInfo',
    'itemTitle',
    'itemTag',
    'viewAll',
    'viewAllIcon',
  ],
  baseStyle: {
    wrap: {
      alignItems: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      gap: '48px',
      maxW: {base: 'full', md: '619px'},
      minW: 0,
      width: 'full',
    },
    section: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      width: 'full',
    },
    heading: {
      color: 'rgba(56, 56, 56, 0.6)',
      fontSize: {base: '10px', md: '10px'},
      fontWeight: 'semibold',
      letterSpacing: {base: '2px', md: '2.4px'},
      lineHeight: '1.2',
      textTransform: 'uppercase',
      width: 'full',
    },
    list: {
      alignItems: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      width: 'full',
      sx: listSiblingHover,
    },
    item: {
      alignItems: 'center',
      bg: 'background.secondary',
      display: 'flex',
      height: 'auto',
      justifyContent: 'stretch',
      minH: '75px',
      pb: {base: 4, md: '13px'},
      pl: {base: 4, md: 5},
      pr: {base: 4, md: 7.5},
      pt: {base: 4, md: '14px'},
      rounded: 'none',
      textDecoration: 'none',
      transition: 'background-color 150ms ease',
      width: 'full',
      _active: {
        bg: 'white',
      },
      _focus: {
        boxShadow: 'none',
      },
      _focusVisible: {
        boxShadow: '0 0 0 3px rgba(27, 26, 24, 0.2)',
      },
      _hover: {
        bg: 'white',
        textDecoration: 'none',
      },
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
      },
    },
    itemInner: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      gap: {base: 2, md: 2.5},
      minW: 0,
      width: 'full',
    },
    itemImage: {
      backgroundColor: 'greyscale90Alpha.10',
      flexShrink: 0,
      h: '48px',
      overflow: 'hidden',
      position: 'relative',
      w: '108px',
    },
    itemInfo: {
      alignItems: {base: 'flex-start', md: 'center'},
      display: 'flex',
      flex: 1,
      flexDirection: {base: 'column', md: 'row'},
      gap: {base: 2, md: 4},
      justifyContent: 'flex-start',
      minW: 0,
      textAlign: 'left',
    },
    itemTitle: {
      color: 'brandColors.darkCharcoal',
      flex: {base: 'unset', md: 1},
      fontSize: 'md',
      fontWeight: 500,
      lineHeight: '1.4',
      minW: 0,
      textAlign: 'left',
      textDecoration: 'none',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      _hover: {
        textDecoration: 'none',
      },
      sx: {
        '& span': {
          fontSize: 'inherit',
          fontWeight: 'inherit',
          lineHeight: 'inherit',
        },
        b: {fontWeight: 500},
        strong: {fontWeight: 500},
      },
    },
    itemTag: {
      color: 'rgba(0, 0, 0, 0.4)',
      flexShrink: 1,
      fontSize: 'xs',
      fontWeight: 'normal',
      lineHeight: '1.2',
      maxW: {base: 'full', md: '170px'},
      minW: 0,
      overflow: 'hidden',
      textAlign: 'left',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    viewAll: {
      alignSelf: 'flex-start',
      color: 'brandColors.primaryDarkLinen',
      cursor: 'pointer',
      display: 'inline-block',
      fontSize: {base: 'md', md: 'md'},
      fontStyle: 'normal',
      fontWeight: '500',
      letterSpacing: 0,
      lineHeight: {'base': '140%', 'md': '130%'},
      m: 0,
      p: 0,
      width: 'auto',
      wordBreak: 'break-word',
      _hover: {
        opacity: 0.85,
      },
    },
    viewAllIcon: {
      boxSize: '16px',
      flexShrink: 0,
    },
  },
  variants: {
    drawer: {
      wrap: {
        flex: {lg: 1},
        gap: {base: '16px', md: '16px', lg: '20px'},
        h: {lg: 'full'},
        maxW: 'full',
        w: 'full',
      },
      section: {
        gap: {base: '12px', md: '12px', lg: '16px'},
      },
      heading: {
        ...header2Search.type.section,
        width: 'full',
      },
      list: {
        alignItems: 'stretch',
        gap: header2Search.space.row,
        sx: header2Search.listHover,
        w: 'full',
      },
      item: {
        alignItems: 'center',
        bg: header2Search.color.rowBg,
        border: 'none',
        boxSizing: 'border-box',
        cursor: 'pointer',
        display: 'flex',
        fontFamily: 'inherit',
        h: {base: '56px', md: '56px', lg: '72px'},
        height: {base: '56px', md: '56px', lg: '72px'},
        justifyContent: 'flex-start',
        maxW: 'full',
        minH: {base: '56px', md: '56px', lg: '72px'},
        overflow: 'hidden',
        pb: 0,
        pl: 0,
        pr: '16px',
        pt: 0,
        rounded: 'none',
        textAlign: 'left',
        textDecoration: 'none',
        w: 'full',
        width: 'full',
        _active: {
          bg: header2Search.color.rowBg,
        },
        _hover: {
          bg: header2Search.color.rowBg,
          opacity: 1,
          textDecoration: 'none',
        },
      },
      itemInner: {
        alignItems: 'center',
        display: 'flex',
        flex: 1,
        gap: {base: '12px', md: '12px', lg: '16px'},
        h: {base: '56px', md: '56px', lg: '72px'},
        minW: 0,
        w: 'full',
      },
      itemImage: header2Search.productThumb,
      itemInfo: {
        alignItems: 'center',
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        gap: {base: '8px', md: '8px', lg: 0},
        h: '20px',
        justifyContent: 'space-between',
        minW: 0,
      },
      itemTitle: {
        ...header2Search.type.productTitle,
        flex: 1,
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        minW: 0,
        overflow: 'hidden',
        textAlign: 'left',
        textDecoration: 'none',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        _hover: {
          textDecoration: 'none',
        },
      },
      itemTag: {
        ...header2Search.type.productCategory,
        flexShrink: 0,
        maxW: {base: '96px', md: '96px', lg: '120px'},
        overflow: 'hidden',
        pe: 0,
        textAlign: 'right',
        textOverflow: 'ellipsis',
        w: {base: '96px', md: '96px', lg: '120px'},
        whiteSpace: 'nowrap',
      },
      viewAll: {
        alignItems: 'center',
        alignSelf: 'flex-start',
        color: 'brandColors.itemMenu',
        display: 'inline-flex',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: {base: '14px', md: '14px', lg: '16px'},
        fontWeight: 500,
        gap: '8px',
        letterSpacing: 0,
        lineHeight: 1.4,
        mb: 0,
        ml: 0,
        mr: 0,
        mt: {base: 0, md: 0, lg: 'auto'},
        p: 0,
        _hover: {
          opacity: 1,
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
        },
      },
      viewAllIcon: {
        boxSize: '16px',
        flexShrink: 0,
        h: '16px',
        transition: 'transform 0.2s ease',
        w: '16px',
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
        },
      },
    },
  },
};
